/**
 * Typed wrappers for the Timesheet module (G3 T.2a) project + assignment
 * endpoints. Paths and shapes are taken VERBATIM from the fixed contract
 * negotiated with `wr-backend-go` (built in parallel against the same
 * contract). The weekly-grid / inbox / state-machine APIs (T.2b/T.2c)
 * are NOT in this file — they land in T.3a / T.3b.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). Every /projects route
 * is fenced server-side by RequireFeature("time_tracking"); the api
 * client turns the backend's 403 { error: "upgrade_required" } into a
 * /pricing redirect, and other 403s reject normally (the caller renders
 * a graceful state).
 *
 * No client-supplied tenant_id is ever sent: the server derives it from
 * the session for every route. The list endpoint is scoped server-side
 * (non-admin → only assigned active projects); this admin client always
 * shows ALL projects because the page itself is HR-admin-only (the
 * backend still re-checks every request — the nav/page gate is
 * presentation only).
 *
 * SECURITY NOTES:
 *  - A cross-tenant employee id in the assignments POST body is rejected
 *    with 400 `{ error: "invalid_employee", field: "employee_ids" }`.
 *    The form bridges this onto the picker (no oracle: the server does
 *    not leak which id was offending, just that one was invalid).
 *  - DELETE is SOFT (sets is_active=false). Historical time_entries
 *    against an inactive project are preserved; new ones cannot be
 *    logged against it server-side.
 */

import { api, blobErrorToApi } from "./client";
import type {
  ApproveWeekRequest,
  CreateProjectRequest,
  GetInboxQuery,
  GetWeekQuery,
  ListInboxResponse,
  ListProjectAssignmentsResponse,
  ListProjectsQuery,
  ListProjectsResponse,
  Project,
  RejectWeekRequest,
  ReplaceProjectAssignmentsRequest,
  TimeEntryInput,
  TimesheetSettings,
  UnlockWeekRequest,
  UpdateProjectRequest,
  UpdateTimesheetSettingsRequest,
  UpsertEntriesRequest,
  WeekResponse,
} from "@/types/timesheet";

export const TIMESHEET_PROJECTS_BASE = "/api/v1/projects";
export const TIMESHEET_BASE = "/api/v1/timesheet";

// ---------------------------------------------------------------------------
// Projects — list / get
// ---------------------------------------------------------------------------

// GET /api/v1/projects?include_inactive=bool — any authed (server-scoped).
// For a non-admin caller the server returns ONLY assigned + active
// projects; the include_inactive query is admin-meaningful (an HR admin
// surfacing soft-deleted rows for reactivation).
export async function listProjects(
  query?: ListProjectsQuery,
  signal?: AbortSignal,
): Promise<ListProjectsResponse> {
  const params: Record<string, string> = {};
  if (query?.include_inactive) params.include_inactive = "true";
  const { data } = await api.get<ListProjectsResponse>(
    TIMESHEET_PROJECTS_BASE,
    {
      params: Object.keys(params).length > 0 ? params : undefined,
      signal,
    },
  );
  return data;
}

// GET /api/v1/projects/{id} — any authed (server-scoped). 404 if the
// caller cannot see it (no existence oracle — same shape as
// /hr/documents).
export async function getProject(
  id: number,
  signal?: AbortSignal,
): Promise<Project> {
  const { data } = await api.get<Project>(
    `${TIMESHEET_PROJECTS_BASE}/${id}`,
    { signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Projects — create / update / soft-delete (HR admin server-side)
// ---------------------------------------------------------------------------

// POST /api/v1/projects — RBAC + CSRF + Audit. The shared client
// injects X-CSRF-Token automatically. Errors:
//   400 validation  (field e.g. `code`/`name`) — wire to the form
//   409 duplicate   — UNIQUE (tenant_id, code) collision; caller toasts
//                     a specific "A project with code ... already
//                     exists" so an HR admin knows the axis.
export async function createProject(
  body: CreateProjectRequest,
): Promise<Project> {
  const { data } = await api.post<Project>(
    TIMESHEET_PROJECTS_BASE,
    body,
  );
  return data;
}

// PATCH /api/v1/projects/{id} — RBAC + CSRF + Audit. Any subset of the
// create body PLUS `is_active`. Pass is_active: true to reactivate a
// soft-deleted project. 409 on a code collision; 400 validation
// otherwise (caller wires `field` to the form input).
export async function updateProject(
  id: number,
  body: UpdateProjectRequest,
): Promise<Project> {
  const { data } = await api.patch<Project>(
    `${TIMESHEET_PROJECTS_BASE}/${id}`,
    body,
  );
  return data;
}

// DELETE /api/v1/projects/{id} — RBAC + CSRF + Audit. SOFT delete
// server-side (flips is_active=false). 204 No Content. Idempotent —
// deleting an already-inactive row is a no-op.
export async function deleteProject(id: number): Promise<void> {
  await api.delete(`${TIMESHEET_PROJECTS_BASE}/${id}`);
}

// ---------------------------------------------------------------------------
// Project assignments — list / replace (HR admin server-side)
// ---------------------------------------------------------------------------

// GET /api/v1/projects/{id}/assignments — HR-admin. Returns the joined
// (employee_id, employee_email, employee_full_name) projection — the UI
// never re-joins the directory for the rendered row.
export async function listAssignments(
  projectId: number,
  signal?: AbortSignal,
): Promise<ListProjectAssignmentsResponse> {
  const { data } = await api.get<ListProjectAssignmentsResponse>(
    `${TIMESHEET_PROJECTS_BASE}/${projectId}/assignments`,
    { signal },
  );
  return data;
}

// POST /api/v1/projects/{id}/assignments — RBAC + CSRF + Audit.
// REPLACE-on-write: the body is the full desired set; the server
// overwrites atomically and returns the new list. A cross-tenant id is
// rejected 400 `{ error: "invalid_employee", field: "employee_ids" }`.
export async function replaceAssignments(
  projectId: number,
  employeeIds: number[],
): Promise<ListProjectAssignmentsResponse> {
  const body: ReplaceProjectAssignmentsRequest = {
    employee_ids: employeeIds,
  };
  const { data } = await api.post<ListProjectAssignmentsResponse>(
    `${TIMESHEET_PROJECTS_BASE}/${projectId}/assignments`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Summary — per-day minutes for a date range. Feeds the personal
// Calendar (`/dashboard/calendar`) so worked-days can render a green
// dot alongside holiday/leave chips. Auth + time_tracking gated; scope
// is always the caller's own rows (no `scope=self` plumbing — it's
// implicit because the endpoint only sums `employee_id = $caller`).
// ---------------------------------------------------------------------------

export interface TimesheetDaySummary {
  /** YYYY-MM-DD (server returns DATE; tz-safe). */
  date: string;
  /** Total logged minutes that day. Always > 0 (server filters HAVING). */
  minutes: number;
}

export interface TimesheetSummaryResponse {
  items: TimesheetDaySummary[];
}

// GET /api/v1/timesheet/summary?from=YYYY-MM-DD&to=YYYY-MM-DD — caller's
// own per-day minute sums. `from` ≤ `to`; range ≤ 92 days. Empty range
// returns `{items: []}`. 400 on missing/malformed dates or out-of-range.
export async function getTimesheetSummary(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<TimesheetSummaryResponse> {
  const { data } = await api.get<TimesheetSummaryResponse>(
    `${TIMESHEET_BASE}/summary`,
    { params: { from, to }, signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Weekly grid — get / upsert entries / submit (G3 T.3a employee surface;
// shapes match the T.2b contract verbatim).
// ---------------------------------------------------------------------------

// GET /api/v1/timesheet/weeks/{weekStart}?employee_id=<optional> — any
// authed (server-scoped: own week by default; manager-of / hr_admin can
// pass employee_id to read a report's week). Auto-creates a `draft`
// shell row when none exists, so a valid week never 404s. 403 when the
// caller is not authorised to view the requested employee_id's week.
export async function getWeek(
  weekStart: string,
  opts?: GetWeekQuery,
  signal?: AbortSignal,
): Promise<WeekResponse> {
  const params: Record<string, string> = {};
  if (opts?.employee_id != null) {
    params.employee_id = String(opts.employee_id);
  }
  const { data } = await api.get<WeekResponse>(
    `${TIMESHEET_BASE}/weeks/${weekStart}`,
    {
      params: Object.keys(params).length > 0 ? params : undefined,
      signal,
    },
  );
  return data;
}

// PUT /api/v1/timesheet/entries — RBAC (own week only) + CSRF + Audit.
// Replace-on-week semantics: the body's `entries` array is the FULL
// desired non-zero set for the week; entries omitted are deleted server-
// side. Returns the same shape as GET (with recomputed totals + the
// rejected→draft auto-unlock reflected). Errors:
//   409 week_locked       — the week is submitted/approved (no unlock yet)
//   400 validation        — field path e.g. `entries[i].minutes`
export async function upsertEntries(
  weekStart: string,
  entries: TimeEntryInput[],
): Promise<WeekResponse> {
  const body: UpsertEntriesRequest = {
    week_start_date: weekStart,
    entries,
  };
  const { data } = await api.put<WeekResponse>(
    `${TIMESHEET_BASE}/entries`,
    body,
  );
  return data;
}

// POST /api/v1/timesheet/weeks/{weekStart}/submit — RBAC (own week) +
// CSRF + Audit. Transitions draft→submitted. No body. Returns the full
// week object with the new status + submitted_at. Errors:
//   409 invalid_state     — week is not in draft (already submitted, etc.)
//   409 empty_week        — no entries to submit
export async function submitWeek(
  weekStart: string,
): Promise<WeekResponse> {
  const { data } = await api.post<WeekResponse>(
    `${TIMESHEET_BASE}/weeks/${weekStart}/submit`,
    null,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Manager approval inbox + state-machine actions (G3 T.3b — built against
// the fixed T.2c contract).
//
// Scope: the inbox returns weeks where the requester is IsManagerOf (the
// target employee) OR hr_admin. Plain employees with no reports get an
// empty list — the UI renders the empty-state panel rather than gating
// the nav entry (per the slice spec, the role gate is the empty list
// itself, not a hidden link).
//
// Manager-of vs HR-admin split on the action surface:
//  - approve / reject: manager-of OR hr_admin (the backend recomputes the
//    relation per request — UI shows the buttons for the same audience).
//  - unlock          : HR-admin ONLY. A manager who could approve is
//    intentionally NOT allowed to "regularise" an already-approved week;
//    the UI hides the button for non-hr_admin and the backend 403s.
// ---------------------------------------------------------------------------

// GET /api/v1/timesheet/inbox?status=… — auth-only; server scopes to the
// caller's reports + hr_admin tenant-wide. Default status="submitted"
// (the "awaiting your approval" mental model); pass "approved" /
// "rejected" for the historical tabs. The shared client turns the
// upgrade_required 403 into a /pricing redirect via the response
// interceptor; other 403s reject normally.
export async function getInbox(
  query?: GetInboxQuery,
  signal?: AbortSignal,
): Promise<ListInboxResponse> {
  const params: Record<string, string> = {};
  if (query?.status) params.status = query.status;
  if (query?.exclude_self) params.exclude_self = "1";
  const { data } = await api.get<ListInboxResponse>(
    `${TIMESHEET_BASE}/inbox`,
    {
      params: Object.keys(params).length > 0 ? params : undefined,
      signal,
    },
  );
  return data;
}

// POST /api/v1/timesheet/weeks/{weekStart}/approve — RBAC (manager-of OR
// hr_admin) + CSRF + Audit. Transitions submitted → approved. Body:
//   { employee_id }    — the target employee (re-validated server-side
//                        against the IsManagerOf chain).
// Returns the full WeekResponse with the new status + approved_by_id +
// approved_at. Errors:
//   409 invalid_state — week is not submitted (already approved /
//                       rejected / draft).
//   403 forbidden     — caller is not manager-of nor hr_admin.
export async function approveWeek(
  weekStart: string,
  employeeId: number,
): Promise<WeekResponse> {
  const body: ApproveWeekRequest = { employee_id: employeeId };
  const { data } = await api.post<WeekResponse>(
    `${TIMESHEET_BASE}/weeks/${weekStart}/approve`,
    body,
  );
  return data;
}

// POST /api/v1/timesheet/weeks/{weekStart}/reject — RBAC (manager-of OR
// hr_admin) + CSRF + Audit. Transitions submitted → rejected. Body:
//   { employee_id, comment }
//     - comment is required — the backend 400s on empty/whitespace with
//       { error: "validation", field: "comment" }. The form trims before
//       sending so a whitespace-only string never reaches the wire.
// Returns the full WeekResponse with the new status + rejection_comment.
// Errors:
//   400 validation (field: "comment") — empty comment.
//   409 invalid_state — week is not submitted.
//   403 forbidden     — caller is not manager-of nor hr_admin.
export async function rejectWeek(
  weekStart: string,
  employeeId: number,
  comment: string,
): Promise<WeekResponse> {
  const body: RejectWeekRequest = {
    employee_id: employeeId,
    comment,
  };
  const { data } = await api.post<WeekResponse>(
    `${TIMESHEET_BASE}/weeks/${weekStart}/reject`,
    body,
  );
  return data;
}

// POST /api/v1/timesheet/weeks/{weekStart}/unlock — HR-admin ONLY +
// CSRF + Audit. Transitions approved → draft (clears approver). Body:
//   { employee_id, comment }
//     - comment is the required regularisation reason (audited).
//     - manager-of alone is NOT enough — the backend 403s a non-hr_admin
//       even when the caller is the original approver. This is
//       administrative, not punitive (week re-opens for the employee to
//       correct), so the UI uses an amber destructive accent — not red.
// Errors:
//   400 validation (field: "comment") — empty comment.
//   409 invalid_state — week is not approved.
//   403 forbidden     — caller is not hr_admin.
export async function unlockWeek(
  weekStart: string,
  employeeId: number,
  comment: string,
): Promise<WeekResponse> {
  const body: UnlockWeekRequest = {
    employee_id: employeeId,
    comment,
  };
  const { data } = await api.post<WeekResponse>(
    `${TIMESHEET_BASE}/weeks/${weekStart}/unlock`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Tenant timesheet settings (085 / docs/47). GET is any-authed (the My
// Time page reads the standard-day fallback for its allocation preview);
// PATCH is hr_admin-gated server-side (router.go hrRole). Defaults are
// resolved server-side — the wire shape is always concrete.
// ---------------------------------------------------------------------------

// GET /api/v1/timesheet/settings — resolved loose/strict + standard-day.
export async function getTimesheetSettings(
  signal?: AbortSignal,
): Promise<TimesheetSettings> {
  const { data } = await api.get<TimesheetSettings>(
    `${TIMESHEET_BASE}/settings`,
    { signal },
  );
  return data;
}

// PATCH /api/v1/timesheet/settings — HR-admin (server-gated) + CSRF +
// Audit. Both fields optional; standard_work_minutes is validated 0..1440
// (400 { error: "validation", field: "standard_work_minutes" }).
export async function updateTimesheetSettings(
  body: UpdateTimesheetSettingsRequest,
): Promise<TimesheetSettings> {
  const { data } = await api.patch<TimesheetSettings>(
    `${TIMESHEET_BASE}/settings`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Period CSV export — GET /timesheet/export.csv?from=&to=
// ---------------------------------------------------------------------------

/**
 * Download the period's time entries as CSV. Scope is server-side: HR sees
 * every employee, anyone else their direct reports. Resolves to a Blob for
 * the shared downloadBlob helper; blobErrorToApi rehydrates a JSON error
 * body so toApiError() reads it normally.
 */
export async function exportTimesheetCSV(
  from: string,
  to: string,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(`${TIMESHEET_BASE}/export.csv`, {
      params: { from, to },
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}
